import React, { useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { api } from "../../utils/api";
import Button from "../../components/atoms/Button";
import Input from "../../components/atoms/Input";
import Select from "../../components/atoms/Select";
import Textarea from "../../components/atoms/Textarea";
import ApproverSelect from "../../components/ApproverSelect";
import Combobox from "../../components/Combobox";
import ProductAutocomplete from "../../components/ProductAutocomplete";
import AccessoryPicker from "../../components/AccessoryPicker";
import CustomerAutocomplete from "../../components/CustomerAutocomplete";
import ProductNameComposer from "../../components/ProductNameComposer";
import ProductNameGuide from "../../components/ProductNameGuide";
import { CLOTHES_NAME_EXAMPLE } from "../../utils/productNameGuide";
import { COUNTRY_LIST, MANUFACTURER_ETC } from "../../utils/variables";

// 매니저어드민 변수와 동일 — 값 불일치 시 서버 검증에서 거부됨
const CATEGORIES = ["WATCH", "BAG", "WALLET", "JEWELRY", "ACCESSORY", "SHOES", "CLOTHES"];
const GRADES = ["NEW", "특A", "A+", "A", "B"];
// 매니저어드민 REGISTER_STATUS_LIST 와 동일 (AS외부6은 자동 세팅이라 제외)
const STATUS_OPTIONS = [
  { value: "1", text: "매입완료" },
  { value: "2", text: "위탁완료" },
  { value: "3", text: "매입실패" },
  { value: "4", text: "이중검수" },
  { value: "5", text: "가품" },
  { value: "7", text: "거래불가" },
];
// 거래방식(trade_type) 셀렉트는 26.9.9 제거 — 매입 유형과 중복, 서버가 매입 유형에서 파생 저장
// 매니저어드민 FUNNEL_RADIO_LIST 와 동일 — 값 불일치 시 서버 검증에서 거부됨
const FUNNELS = [
  "자사몰",
  "블로그",
  "파워링크",
  "카페/지식인",
  "플레이스",
  "지인소개",
  "매장유입",
  "기존고객",
  "기타",
];
// 매입 유형 — 매니저어드민 PURCHASE_TYPE_RADIO_LIST 와 동일 (설문 발송 규칙 키). 기본 방문매입 (26.9.7)
const PURCHASE_TYPES = [
  { value: "visit", label: "방문매입" },
  { value: "parcel", label: "택배매입" },
  { value: "event", label: "행사매입" },
  { value: "etc", label: "기타" },
];
// 고객유형 — 매니저어드민 상담일지 고객유형(신규/기존/확인불가)과 동일 3분기.
// 신규는 정보만 입력해 요청하고, 실제 고객 생성은 매니저어드민 최종 등록 시(중복확인 후) 수행된다.
const CUSTOMER_TYPES = [
  { value: "new", label: "신규" },
  { value: "existing", label: "기존고객" },
  { value: "unable", label: "확인불가" },
];

const onlyDigits = (s) => String(s ?? "").replace(/[^0-9]/g, "");
const withComma = (s) => {
  const d = onlyDigits(s);
  return d ? Number(d).toLocaleString() : "";
};
const statusText = (v) =>
  STATUS_OPTIONS.find((s) => s.value === String(v))?.text || "";

// 리스트 행 요약: 카테고리 · 등급 · (거래구분별 가격) · 구성품
// 거래구분 매입완료(1)→매입가, 위탁완료(2)→위탁가, 그 외엔 가격 생략
const rowSummary = (p) => {
  const segs = [];
  if (p.category) segs.push(p.category);
  if (p.rating) segs.push(p.rating);
  const st = String(p.status);
  if (st === "1" && onlyDigits(p.purchasePrice))
    segs.push(`매입 ${Number(onlyDigits(p.purchasePrice)).toLocaleString()}`);
  else if (st === "2" && onlyDigits(p.consignmentPrice))
    segs.push(`위탁 ${Number(onlyDigits(p.consignmentPrice)).toLocaleString()}`);
  if (p.accessories && p.accessories.trim()) segs.push(p.accessories.trim());
  return segs.join(" · ");
};

const emptyProduct = () => ({
  category: "",
  brandId: "",
  productName: "",
  // 의류 상품명 구성 입력의 성별 — 상담 등록요청 단계에선 상품명 조합에만 사용(서버 미전송)
  gender: "",
  serialNo: "",
  // 제조국 셀렉트 값(COUNTRY_LIST 항목 또는 직접입력) — 매니저어드민 상담일지 등록과 동일 구성
  manufacturer: "",
  // 제조국 직접입력 텍스트(manufacturer === 직접입력 일 때만 전송)
  manufacturerEtc: "",
  rating: "",
  buyAddress: "",
  buyDate: "",
  accessories: "",
  accessoryEtc: "",
  remarks: "",
  status: "1",
  purchasePrice: "",
  consignmentPrice: "",
  storePrice: "",
  price: "",
});

const productValid = (p) =>
  !!p.category &&
  !!p.brandId &&
  p.productName.trim() !== "" &&
  !!p.rating &&
  onlyDigits(p.purchasePrice) !== "" &&
  onlyDigits(p.consignmentPrice) !== "" &&
  onlyDigits(p.storePrice) !== "" &&
  onlyDigits(p.price) !== "";

// 편집 중인 draft 에 의미있는 입력이 있는지 (미저장 데이터 손실 방지용)
const productStarted = (p) =>
  p.productName.trim() !== "" ||
  p.brandId !== "" ||
  p.category !== "" ||
  p.rating !== "" ||
  p.serialNo.trim() !== "" ||
  p.manufacturer.trim() !== "" ||
  p.manufacturerEtc.trim() !== "" ||
  p.buyAddress.trim() !== "" ||
  p.buyDate !== "" ||
  p.accessories.trim() !== "" ||
  p.remarks.trim() !== "" ||
  onlyDigits(p.purchasePrice) !== "" ||
  onlyDigits(p.consignmentPrice) !== "" ||
  onlyDigits(p.storePrice) !== "" ||
  onlyDigits(p.price) !== "";

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function ConsultationRequestForm() {
  const navigate = useNavigate();
  const { data: me } = useSWR("/manager/change-request/me");
  const { data: allBranches } = useSWR(
    "/manager/change-request/branches?type=work"
  );
  const { data: brands } = useSWR("/manager/change-request/consultation-brands");

  // 고객유형: new(신규) | existing(기존고객) | unable(확인불가)
  const [customerType, setCustomerType] = useState("new");
  const [userName, setUserName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [bankUser, setBankUser] = useState("");
  // 신규 연락처 중복확인: null=미확인, "ok"=신규 등록 가능, "dup"=이미 등록된 연락처
  const [dupStatus, setDupStatus] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  // 기존고객 연동 — 검색으로 선택한 매입/위탁 고객
  const [existingUser, setExistingUser] = useState(null);
  const [branchName, setBranchName] = useState("");
  const [consultDate, setConsultDate] = useState(todayStr);
  const [funnel, setFunnel] = useState("");
  const [purchaseType, setPurchaseType] = useState("visit");

  // 추가된 상품 리스트(committed) + 현재 편집중 draft + 편집 인덱스
  const [products, setProducts] = useState([]);
  const [draft, setDraft] = useState(emptyProduct);
  const [editIndex, setEditIndex] = useState(null);
  const [productErr, setProductErr] = useState("");

  const [reason, setReason] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  const editorRef = useRef(null);

  // 담당지점: 등록요청 담당자는 여러 지점의 상담을 대신 등록하는 중앙 역할이므로
  // 본인 manageradmin 지점 권한과 무관하게 전체 근무(work) 지점 중에서 선택한다.
  const branchOptions = useMemo(
    () => (Array.isArray(allBranches) ? allBranches.map((b) => b.name) : []),
    [allBranches]
  );

  const brandList = Array.isArray(brands) ? brands : [];
  const brandOptions = useMemo(
    () =>
      (Array.isArray(brands) ? brands : []).map((b) => ({
        value: String(b.id),
        label: `${b.brand_kr}${b.brand_eng ? ` (${b.brand_eng})` : ""}`,
      })),
    [brands]
  );
  const brandLabelOf = (brandId) =>
    brandOptions.find((o) => o.value === String(brandId))?.label || "(브랜드)";
  // 의류 상품명 구성 입력에 자동 포함되는 브랜드(한글) — 선택된 브랜드의 brand_kr
  const draftBrandKr =
    brandList.find((b) => String(b.id) === String(draft.brandId))?.brand_kr ||
    "";

  // ----- 고객유형 -----
  const switchCustomerType = (type) => {
    if (type === customerType) return;
    setCustomerType(type);
    // 유형 전환 시 확인 상태만 초기화. 입력값(고객명/연락처/계좌)은 유지 — 오터치로 탭을 오갔다가
    // 돌아와도 입력이 사라지지 않게 한다. 제출 페이로드에서 유형별로 배제하므로 잔존값 전송 위험 없음.
    setDupStatus(null);
    setExistingUser(null);
    setSubmitErr("");
  };

  // 신규 연락처 중복확인 — 매니저어드민 상담일지의 중복확인과 동일 흐름.
  // 이미 등록된 연락처면 기존고객으로 연동하도록 안내한다.
  const checkDuplicate = async () => {
    const digits = onlyDigits(userPhone);
    if (!digits) {
      setSubmitErr("연락처를 입력해주세요.");
      return;
    }
    if (dupChecking) return;
    setDupChecking(true);
    setSubmitErr("");
    try {
      const { data } = await api.get(
        `/manager/change-request/consultation-user-check?phone=${digits}`
      );
      if (!data?.success) {
        setSubmitErr(data?.alert || "중복 확인에 실패했습니다.");
        return;
      }
      setDupStatus(data?.data?.duplicate ? "dup" : "ok");
    } catch (err) {
      setSubmitErr("서버에 연결할 수 없습니다.");
    } finally {
      setDupChecking(false);
    }
  };

  // ----- draft(편집폼) 조작 -----
  const setDraftField = (key, value) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const patchDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const commitDraft = () => {
    if (!productValid(draft)) {
      setProductErr(
        "필수 항목(카테고리·브랜드·상품명·등급·4가격)을 입력해주세요."
      );
      return;
    }
    setProducts((prev) =>
      editIndex === null
        ? [...prev, draft]
        : prev.map((p, i) => (i === editIndex ? draft : p))
    );
    setDraft(emptyProduct());
    setEditIndex(null);
    setProductErr("");
  };

  const editProduct = (i) => {
    setDraft({ ...products[i] });
    setEditIndex(i);
    setProductErr("");
    setTimeout(
      () =>
        editorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      0
    );
  };

  const deleteProduct = (i) => {
    setProducts((prev) => prev.filter((_, idx) => idx !== i));
    if (editIndex === i) {
      setDraft(emptyProduct());
      setEditIndex(null);
      setProductErr("");
    } else if (editIndex !== null && i < editIndex) {
      setEditIndex(editIndex - 1);
    }
  };

  const cancelEdit = () => {
    setDraft(emptyProduct());
    setEditIndex(null);
    setProductErr("");
  };

  const draftDirty = editIndex !== null || productStarted(draft);

  // 고객 입력 완결 조건 — 신규: 중복확인(등록 가능) 필수, 기존고객: 연동 선택 필수, 확인불가: 이름/연락처만
  const customerOk =
    customerType === "existing"
      ? !!existingUser
      : userName.trim() !== "" &&
        onlyDigits(userPhone) !== "" &&
        (customerType !== "new" || dupStatus === "ok");

  const canSubmit =
    !!me &&
    customerOk &&
    branchName !== "" &&
    funnel !== "" &&
    /^\d{4}-\d{2}-\d{2}$/.test(consultDate) &&
    products.length > 0 &&
    !!reviewerId &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitErr("");
    // 미저장 상품 보호 — draft 를 '추가'/'수정 완료' 하기 전엔 제출 차단(데이터 손실 방지)
    if (draftDirty) {
      setSubmitErr(
        editIndex !== null
          ? "수정 중인 상품을 '수정 완료' 해주세요."
          : "작성 중인 상품을 '추가' 해주세요."
      );
      return;
    }
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/manager/change-request", {
        type: "consultation_registration",
        after_data: {
          customer_type: customerType,
          // 기존고객: 연동 id 만 보내고 이름/연락처/계좌는 서버가 고객 마스터에서 스냅샷/재조회
          consultation_user_id:
            customerType === "existing" ? existingUser?.id : null,
          user_name:
            customerType === "existing"
              ? String(existingUser?.user_name || "")
              : userName.trim(),
          user_phone:
            customerType === "existing"
              ? String(existingUser?.user_phone || "")
              : userPhone.trim(),
          // 계좌정보는 신규만 입력 (확인불가는 미수집 — 매니저어드민과 동일)
          bank_name: customerType === "new" ? bankName.trim() || null : null,
          bank_account:
            customerType === "new" ? bankAccount.trim() || null : null,
          bank_user: customerType === "new" ? bankUser.trim() || null : null,
          branch_name: branchName,
          consultation_date: consultDate,
          funnel,
          purchase_type: purchaseType,
          products: products.map((p) => ({
            category: p.category,
            brand_id: Number(p.brandId),
            product_name: p.productName.trim(),
            serial_no: p.serialNo.trim() || null,
            manufacturer:
              (p.manufacturer === MANUFACTURER_ETC
                ? p.manufacturerEtc.trim()
                : p.manufacturer.trim()) || null,
            rating: p.rating,
            buy_address: p.buyAddress.trim() || null,
            buy_date: p.buyDate || null,
            accessories: p.accessories.trim() || null,
            // 기타가 선택된 경우에만 자유입력 전송(해제 후 잔존값 차단)
            accessory_etc:
              (p.accessories.includes("기타") ? p.accessoryEtc.trim() : "") ||
              null,
            remarks: p.remarks.trim() || null,
            status: Number(p.status),
            purchase_price: onlyDigits(p.purchasePrice),
            consignment_price: onlyDigits(p.consignmentPrice),
            store_price: onlyDigits(p.storePrice),
            price: onlyDigits(p.price),
          })),
        },
        reason: reason.trim() || null,
        reviewer_id: reviewerId,
      });
      if (!data?.success) {
        setSubmitErr(data?.alert || "요청을 보내지 못했습니다.");
        return;
      }
      navigate("/sent", { replace: true });
    } catch (err) {
      setSubmitErr("서버에 연결할 수 없습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  // 직접 URL 접근 차단 — 지정 담당자(is_consult_register)만 작성 가능 (서버에서도 차단)
  if (me && !me.is_consult_register) {
    return (
      <Denied>
        상담일지 등록요청 권한이 없습니다.
        <br />
        관리자에게 문의해주세요.
      </Denied>
    );
  }

  return (
    <Wrapper>
      <Notice>
        상담일지를 작성해 승인 요청합니다.
        <br />
        <SoftNotice>
          승인되면 매니저어드민 임시저장 목록으로 넘어가며, 최종 등록은
          매니저어드민에서 진행됩니다.
        </SoftNotice>
      </Notice>

      <Form onSubmit={submit}>
        <GroupTitle>고객 정보</GroupTitle>
        <Section>
          <Label>고객 유형 *</Label>
          <TypeTabs>
            {CUSTOMER_TYPES.map((t) => (
              <TypeTab
                key={t.value}
                type="button"
                active={customerType === t.value}
                onClick={() => switchCustomerType(t.value)}
              >
                {t.label}
              </TypeTab>
            ))}
          </TypeTabs>
          {customerType === "new" && (
            <TypeHint>
              신규 고객은 승인 시 바로 생성되지 않습니다. 매니저어드민 최종
              등록 시 중복확인 후 고객으로 등록됩니다.
            </TypeHint>
          )}
        </Section>

        {customerType === "existing" ? (
          <>
            <Section>
              <Label>고객 검색 *</Label>
              <CustomerAutocomplete
                onSelect={(item) => {
                  setExistingUser(item);
                  setSubmitErr("");
                }}
              />
            </Section>
            {existingUser && (
              <SelectedCustomer>
                <SelectedRow>
                  <SelectedLabel>고객명</SelectedLabel>
                  <SelectedValue>{existingUser.user_name || "-"}</SelectedValue>
                </SelectedRow>
                <SelectedRow>
                  <SelectedLabel>연락처</SelectedLabel>
                  <SelectedValue>{existingUser.user_phone || "-"}</SelectedValue>
                </SelectedRow>
                <SelectedRow>
                  <SelectedLabel>입금계좌</SelectedLabel>
                  <SelectedValue>
                    {existingUser.bank_info && existingUser.bank_info.trim()
                      ? existingUser.bank_info.trim()
                      : "-"}
                  </SelectedValue>
                </SelectedRow>
                <ClearBtn type="button" onClick={() => setExistingUser(null)}>
                  선택 해제
                </ClearBtn>
              </SelectedCustomer>
            )}
          </>
        ) : (
          <>
            <Section>
              <Label>고객명 *</Label>
              <Input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="고객명"
              />
            </Section>
            <Section>
              <Label>연락처 *</Label>
              <PhoneRow>
                <Input
                  value={userPhone}
                  inputMode="numeric"
                  onChange={(e) => {
                    setUserPhone(e.target.value);
                    setDupStatus(null); // 연락처가 바뀌면 중복확인 다시
                  }}
                  placeholder="01012345678"
                />
                {customerType === "new" && (
                  <DupBtn
                    type="button"
                    onClick={checkDuplicate}
                    disabled={dupChecking}
                  >
                    {dupChecking ? "확인 중..." : "중복확인"}
                  </DupBtn>
                )}
              </PhoneRow>
              {customerType === "new" && dupStatus === "ok" && (
                <DupOk>등록되지 않은 연락처입니다. 이어서 작성해주세요.</DupOk>
              )}
              {customerType === "new" && dupStatus === "dup" && (
                <DupWarn>
                  이미 등록된 연락처입니다. 고객 유형을 기존고객으로 선택해
                  연동해주세요.
                </DupWarn>
              )}
            </Section>
            {customerType === "new" && (
              <>
                <Row2>
                  <Section>
                    <Label>은행</Label>
                    <Input
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="은행"
                    />
                  </Section>
                  <Section>
                    <Label>예금주</Label>
                    <Input
                      value={bankUser}
                      onChange={(e) => setBankUser(e.target.value)}
                      placeholder="예금주"
                    />
                  </Section>
                </Row2>
                <Section>
                  <Label>계좌번호</Label>
                  <Input
                    value={bankAccount}
                    onChange={(e) => setBankAccount(e.target.value)}
                    placeholder="계좌번호 (콤마 없이)"
                  />
                </Section>
              </>
            )}
          </>
        )}

        <GroupTitle>상담 정보</GroupTitle>
        <Row2>
          <Section>
            <Label>담당지점 *</Label>
            <Select
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
            >
              <option value="">선택</option>
              {branchOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Section>
          <Section>
            <Label>상담일자 *</Label>
            <Input
              type="date"
              value={consultDate}
              onChange={(e) => setConsultDate(e.target.value)}
            />
          </Section>
        </Row2>
        <Section>
          <Label>유입경로 *</Label>
          <Select value={funnel} onChange={(e) => setFunnel(e.target.value)}>
            <option value="">선택</option>
            {FUNNELS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </Section>
        <Section>
          <Label>매입 유형</Label>
          <Select value={purchaseType} onChange={(e) => setPurchaseType(e.target.value)}>
            {PURCHASE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Section>

        <GroupTitle>상품 ({products.length})</GroupTitle>

        {/* 추가된 상품 리스트 — 클릭하면 아래 편집폼으로 불러와 수정 */}
        {products.length > 0 && (
          <ProductList>
            {products.map((p, i) => (
              <ProductRow key={i} editing={editIndex === i}>
                <RowMain type="button" onClick={() => editProduct(i)}>
                  <RowTop>
                    <RowName>
                      {brandLabelOf(p.brandId)} · {p.productName}
                    </RowName>
                    <RowBadge status={p.status}>
                      {statusText(p.status)}
                    </RowBadge>
                  </RowTop>
                  <RowSub>{rowSummary(p)}</RowSub>
                </RowMain>
                <RowDel type="button" onClick={() => deleteProduct(i)}>
                  삭제
                </RowDel>
              </ProductRow>
            ))}
          </ProductList>
        )}

        {/* 상품 편집폼 (추가 / 수정) */}
        <ProductCard ref={editorRef} editing={editIndex !== null}>
          <ProductHead>
            <ProductNo>
              {editIndex === null ? "상품 추가" : `상품 수정 (${editIndex + 1}번)`}
            </ProductNo>
            {editIndex !== null && (
              <RemoveBtn type="button" onClick={cancelEdit}>
                취소
              </RemoveBtn>
            )}
          </ProductHead>

          <Row2>
            <Section>
              <Label>카테고리 *</Label>
              <Select
                value={draft.category}
                onChange={(e) => setDraftField("category", e.target.value)}
              >
                <option value="">선택</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Section>
            <Section>
              <Label>브랜드 *</Label>
              <Combobox
                options={brandOptions}
                value={draft.brandId}
                onChange={(v) => setDraftField("brandId", v)}
                disabled={!brandList.length}
                placeholder={brands == null ? "불러오는 중..." : "브랜드 검색"}
              />
            </Section>
          </Row2>

          {/* 의류 상품명 구성 입력 — 편집 대상이 바뀌면 remount 해 이전 상품의 구성 조각이 남지 않게 한다 */}
          <ProductNameComposer
            key={editIndex === null ? "new" : `edit-${editIndex}`}
            category={draft.category}
            name={draft.productName}
            brandKr={draftBrandKr}
            gender={draft.gender}
            onGenderChange={(v) => setDraftField("gender", v)}
            onCompose={(v) => setDraftField("productName", v)}
          />

          <Section>
            <Label>상품명 *</Label>
            <ProductAutocomplete
              value={draft.productName}
              onChange={(v) => setDraftField("productName", v)}
              onSelect={(item) => {
                const patch = { productName: item.product_name };
                if (CATEGORIES.includes(item.category))
                  patch.category = item.category;
                // 브랜드는 활성 목록에 있을 때만 채움(삭제 브랜드 등 서버 거부 방지)
                const bid = item.brand_id ? String(item.brand_id) : "";
                if (bid && brandOptions.some((o) => o.value === bid))
                  patch.brandId = bid;
                patchDraft(patch);
              }}
              placeholder={
                draft.category === "CLOTHES"
                  ? `예: ${CLOTHES_NAME_EXAMPLE}`
                  : "상품명 (입력 시 자동완성)"
              }
            />
            <ProductNameGuide
              category={draft.category}
              name={draft.productName}
              gender={draft.gender}
            />
          </Section>

          <Row2>
            <Section>
              <Label>등급 *</Label>
              <Select
                value={draft.rating}
                onChange={(e) => setDraftField("rating", e.target.value)}
              >
                <option value="">선택</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </Section>
            <Section>
              <Label>거래구분 *</Label>
              <Select
                value={draft.status}
                onChange={(e) => setDraftField("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.text}
                  </option>
                ))}
              </Select>
            </Section>
          </Row2>

          <Row2>
            <Section>
              <Label>시리얼번호</Label>
              <Input
                value={draft.serialNo}
                onChange={(e) => setDraftField("serialNo", e.target.value)}
              />
            </Section>
            <Section>
              <Label>제조국</Label>
              <Select
                value={draft.manufacturer}
                onChange={(e) =>
                  patchDraft({ manufacturer: e.target.value, manufacturerEtc: "" })
                }
              >
                <option value="">선택</option>
                {COUNTRY_LIST.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value={MANUFACTURER_ETC}>{MANUFACTURER_ETC}</option>
              </Select>
              {draft.manufacturer === MANUFACTURER_ETC && (
                <Input
                  value={draft.manufacturerEtc}
                  placeholder="제조국 직접입력"
                  onChange={(e) =>
                    setDraftField("manufacturerEtc", e.target.value)
                  }
                />
              )}
            </Section>
          </Row2>

          <Row2>
            <Section>
              <Label>구매처</Label>
              <Input
                value={draft.buyAddress}
                onChange={(e) => setDraftField("buyAddress", e.target.value)}
              />
            </Section>
            <Section>
              <Label>구매일</Label>
              <Input
                type="date"
                value={draft.buyDate}
                onChange={(e) => setDraftField("buyDate", e.target.value)}
              />
            </Section>
          </Row2>

          <PriceGrid>
            <Section>
              <Label>매입가 *</Label>
              <Input
                value={draft.purchasePrice}
                inputMode="numeric"
                onChange={(e) =>
                  setDraftField("purchasePrice", withComma(e.target.value))
                }
                placeholder="0"
              />
            </Section>
            <Section>
              <Label>위탁가 *</Label>
              <Input
                value={draft.consignmentPrice}
                inputMode="numeric"
                onChange={(e) =>
                  setDraftField("consignmentPrice", withComma(e.target.value))
                }
                placeholder="0"
              />
            </Section>
            <Section>
              <Label>매장가 *</Label>
              <Input
                value={draft.storePrice}
                inputMode="numeric"
                onChange={(e) =>
                  setDraftField("storePrice", withComma(e.target.value))
                }
                placeholder="0"
              />
            </Section>
            <Section>
              <Label>판매가 *</Label>
              <Input
                value={draft.price}
                inputMode="numeric"
                onChange={(e) =>
                  setDraftField("price", withComma(e.target.value))
                }
                placeholder="0"
              />
            </Section>
          </PriceGrid>

          <Section>
            <Label>구성품</Label>
            <AccessoryPicker
              value={draft.accessories}
              onChange={(v) => setDraftField("accessories", v)}
              etc={draft.accessoryEtc}
              onEtcChange={(v) => setDraftField("accessoryEtc", v)}
            />
          </Section>
          <Section>
            <Label>비고</Label>
            <Textarea
              value={draft.remarks}
              onChange={(e) => setDraftField("remarks", e.target.value)}
              placeholder="비고"
            />
          </Section>

          {productErr && <ErrorText>{productErr}</ErrorText>}

          <CommitBtn type="button" editing={editIndex !== null} onClick={commitDraft}>
            {editIndex === null ? "+ 상품 추가" : "수정 완료"}
          </CommitBtn>
        </ProductCard>

        <GroupTitle>요청</GroupTitle>
        <Section>
          <Label>사유 (선택)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="요청 사유"
          />
        </Section>
        <Section>
          <Label>승인자 *</Label>
          <ApproverSelect
            type="consultation_registration"
            value={reviewerId}
            onChange={setReviewerId}
          />
        </Section>

        {submitErr && <ErrorText>{submitErr}</ErrorText>}

        <Button type="submit" block disabled={!canSubmit}>
          {submitting ? "전송 중..." : "상담일지 등록 요청 보내기"}
        </Button>
      </Form>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Notice = styled.div`
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  color: #003580;
  background: #eef3fb;
  border: 1px solid #d6e0f0;
  border-radius: 12px;
`;
const SoftNotice = styled.span`
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const GroupTitle = styled.div`
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #003580;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const TypeTabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
`;
const TypeTab = styled.button`
  padding: 11px 0;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active }) => (active ? "#fff" : "#555")};
  background: ${({ active }) => (active ? "#003580" : "#fff")};
  border: 1px solid ${({ active }) => (active ? "#003580" : "#d9dde2")};
  border-radius: 10px;
  cursor: pointer;
`;
const TypeHint = styled.p`
  font-size: 12px;
  color: #888;
  line-height: 1.5;
`;
const PhoneRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  & > div:first-of-type,
  & > input:first-of-type {
    flex: 1;
  }
`;
const DupBtn = styled.button`
  flex-shrink: 0;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  color: #003580;
  background: #eef3fb;
  border: 1px solid #c9d8ef;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
const DupOk = styled.p`
  font-size: 12px;
  color: #0a7d3a;
`;
const DupWarn = styled.p`
  font-size: 12px;
  color: #fb4d44;
`;
const SelectedCustomer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: #f5f8fd;
  border: 1px solid #c9d8ef;
  border-radius: 12px;
`;
const SelectedRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 10px;
  font-size: 14px;
`;
const SelectedLabel = styled.span`
  color: #888;
  font-size: 13px;
`;
const SelectedValue = styled.span`
  color: #222;
  font-weight: 500;
  word-break: break-word;
`;
const ClearBtn = styled.button`
  align-self: flex-end;
  padding: 5px 14px;
  font-size: 12px;
  color: #666;
  background: #fff;
  border: 1px solid #d9dde2;
  border-radius: 8px;
  cursor: pointer;
`;
const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;
const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;
const Label = styled.label`
  font-size: 13px;
  color: #555;
`;
const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ProductRow = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ editing }) => (editing ? "#003580" : "#e6e8eb")};
  border-radius: 12px;
  background: ${({ editing }) => (editing ? "#f5f8fd" : "#fff")};
  overflow: hidden;
`;
const RowMain = styled.button`
  flex: 1;
  text-align: left;
  padding: 12px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
const RowName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #222;
  word-break: break-word;
`;
const RowBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 8px;
  color: ${({ status }) =>
    status === "1" || status === 1
      ? "#0a7d3a"
      : status === "2" || status === 2
      ? "#0056b3"
      : status === "5" || status === 5 || status === "7" || status === 7
      ? "#c0392b"
      : "#777"};
  background: ${({ status }) =>
    status === "1" || status === 1
      ? "#e6f5ec"
      : status === "2" || status === 2
      ? "#e7f0fb"
      : status === "5" || status === 5 || status === "7" || status === 7
      ? "#fdecea"
      : "#f0f2f5"};
`;
const RowSub = styled.div`
  font-size: 12px;
  color: #888;
`;
const RowDel = styled.button`
  flex-shrink: 0;
  padding: 0 14px;
  font-size: 12px;
  color: #fb4d44;
  background: #fff;
  border: none;
  border-left: 1px solid #f0e0df;
  cursor: pointer;
`;
const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid ${({ editing }) => (editing ? "#003580" : "#e6e8eb")};
  border-radius: 12px;
`;
const ProductHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const ProductNo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #222;
`;
const RemoveBtn = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  color: #666;
  background: #fff;
  border: 1px solid #d9dde2;
  border-radius: 8px;
  cursor: pointer;
`;
const CommitBtn = styled.button`
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: ${({ editing }) => (editing ? "#003580" : "#0a66c2")};
  border: none;
  border-radius: 12px;
  cursor: pointer;
`;
const ErrorText = styled.div`
  color: #fb4d44;
  font-size: 13px;
`;
const Denied = styled.div`
  text-align: center;
  color: #888;
  font-size: 14px;
  line-height: 1.7;
  padding: 48px 16px;
`;

export default ConsultationRequestForm;
